import { isUndefined, omitBy } from 'lodash-es'
import type {
  SearchApiSuggestion,
  SearchApiSuggestionType,
} from '~/api/search-api.types'

export type AutosuggestItem = {
  type: SearchApiSuggestionType
  code: string
  label: string
  flag: string
  path: string
  selected: boolean
}

export const SUGGESTIONS_DEBOUNCE = 300

const TYPES_ORDER = ['C', 'R', 'P', 'H', 'A', 'B', 'G']

export function flatSuggestions(
  items: SearchApiSuggestion[],
): SearchApiSuggestion[] {
  return items
    .filter(({ type }) => TYPES_ORDER.includes(type))
    .toSorted(
      (a, b) => TYPES_ORDER.indexOf(a.type) - TYPES_ORDER.indexOf(b.type),
    )
    .flatMap((item) => [
      item,
      ...(item.type === 'C' ? (item.topRegions ?? []) : []),
      ...(item.type === 'R' ? (item.topPlaces ?? []) : []),
    ])
}

/**
 * TODO: Remove suggestion normalization once search-api has consistency in suggestion objects
 * @see https://jira.migros.net/browse/IHGWEBCC-2370
 */
export function normalizeSuggestion(
  item: SearchApiSuggestion,
): SearchApiSuggestion {
  const { code, country } = item

  return {
    ...item,
    code: code.startsWith(country) ? code : `${country}${code}`,
  }
}

export function mapDestinationToSuggestion(
  destination: ConfDestination | undefined,
) {
  if (!destination) return

  const { urlPath } = destination
  const { country, region, place } = getDestinationQuery(destination)

  if (!urlPath || !country) return

  const slug = removeLanguagePrefix(urlPath)
    .split('/')
    .filter(Boolean)
    .slice(0, place ? 3 : region ? 2 : 1)
    .join('/')

  return omitBy(
    {
      type: place ? 'P' : region ? 'R' : 'C',
      code: place ?? region ?? country ?? '',
      label: getDestinationName(destination),
      country,
      region,
      slug,
    } satisfies SearchApiSuggestion,
    isUndefined,
  ) as unknown as SearchApiSuggestion
}
